/**
 * Feeder — the one thing in this game you can win, and the pilot fish you win.
 *
 * A bright fish appears somewhere in the lagoon every so often. Swim into it and it is yours;
 * a golden pilot fish falls in behind you and stays there until the next feeder is taken by
 * somebody. That is the whole loop, and it is deliberately the whole loop: the reward is being
 * SEEN to have won, not a number going up. Everyone in the bay renders the pilot fish on
 * whoever holds it, so the prize is a thing other people can look at.
 *
 * WHY THIS FILE IS ONLY THE PRESENTATION
 * Who ate it is the first question in this game with a wrong answer, and two clients that both
 * think they got there first is exactly the dispute the relay was built not to have. So the
 * relay arbitrates — first claim wins, everyone is told — and this file draws whatever it is
 * told. Solo runs the same protocol against a local stand-in, so there is one code path and
 * not two.
 *
 * THE FISH IS BUILT, NOT LOADED, like everything else. Two of them: the feeder itself, and the
 * pilot fish, which is the same body under a gold palette at half the size.
 */
import * as THREE from 'three';
import { Rng } from '../core/Rng.ts';
import { SharkMaterial, swimParamsForSpeed } from '../render/SharkMaterial.ts';
import { buildFish, paint } from '../world/fish/proceduralFish.js';
import { bedHeight, waterDepth, COAST } from '../world/Coast.ts';
import type { SkyEnv } from '../world/DayNight.ts';

/** how close counts as eaten, in world units, when just swimming into it */
const REACH = 2.6;
/** and while the jaws are closing — a lunge should be worth something */
const REACH_BITING = 4.4;
/** the feeder's own size, u */
const FEEDER_LEN = 1.5;
/** the pilot fish rides this far behind and beside its holder */
const PILOT_BACK = 2.1;
const PILOT_SIDE = 0.9;
const PILOT_UP = 0.55;

/** where a feeder may appear: the lagoon and the back-reef, never the drop-off */
const SPAWN_Z: [number, number] = [22, 74];
const SPAWN_X = 150;

export interface FeederSpawn {
  id: number;
  x: number;
  y: number;
  z: number;
}

/**
 * Pick where feeder `id` appears, from the world seed alone.
 *
 * DERIVED, NOT SENT, and the relay derives it the same way — so a client that misses the spawn
 * packet and catches the next one is still looking at the same fish in the same place, and a
 * relay restart mid-round does not move it. The same trick the bay codes use.
 */
export function feederAt(worldSeed: number, id: number): FeederSpawn {
  const rng = new Rng(((worldSeed ^ Math.imul(id + 1, 0x9e3779b9)) >>> 0) || 1);
  const x = rng.range(-SPAWN_X, SPAWN_X);
  const z = rng.range(SPAWN_Z[0], SPAWN_Z[1]);
  // Mid-water over the bed, and never inside it.
  const bed = bedHeight(x, z);
  const depth = Math.max(2.5, waterDepth(x, z));
  return { id, x, y: bed + Math.min(depth * 0.55, 5.5) + 0.6, z };
}

/**
 * One fish, built and dressed.
 *
 * The generator hands back a triangle mesh, NOT a voxel cloud — so there is no meshing to do
 * here and no palette to quantise. (I wrote the quantise-and-greedy-mesh version first, which
 * is the right shape for the shark forge and completely wrong for this: `buildFish` has
 * already done the hard part.) All that is left is the two conversions this pipeline needs.
 */
function makeFish(seed: number, palette: number, pattern: number, scale: number): {
  geometry: THREE.BufferGeometry; spine: number;
} {
  const m = buildFish({
    plan: 'fusiform', seed, depth: 1.05, compress: 0.95, fins: 1.05, tail: 1, NT: 30, NA: 16,
  });
  paint(m, { palette, pattern, patn: 3, contrast: 0.85 });

  // Display space in, LINEAR out: the generator authors livery the way a person picks colours,
  // and this pipeline shades in linear with ACES on the end.
  const col = m.colors;
  for (let i = 0; i < col.length; i++) {
    const c = col[i];
    col[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }

  // spineT off the generator's own body coordinate: already 0 at the nose, 1 at the peduncle,
  // which is what the swim shader wants. Fins overshoot slightly past 1, so clamp.
  const nVerts = m.positions.length / 3;
  const spineT = new Float32Array(nVerts);
  for (let i = 0; i < nVerts; i++) {
    const t = m.body[i * 4];
    spineT[i] = t < 0 ? 0 : t > 1 ? 1 : t;
  }

  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(m.positions, 3));
  g.setAttribute('normal', new THREE.BufferAttribute(m.normals, 3));
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  g.setAttribute('spineT', new THREE.BufferAttribute(spineT, 1));
  g.setIndex(new THREE.BufferAttribute(m.indices, 1));
  // Built nose-at-+X; this world is nose-at-+Z.
  g.rotateY(-Math.PI / 2);
  g.scale(scale, scale, scale);
  g.computeBoundingSphere();
  if (g.boundingSphere) g.boundingSphere.radius *= 1.8;
  return { geometry: g, spine: m.len * scale };
}

export class Feeder {
  readonly group = new THREE.Group();

  private readonly feederMesh: THREE.Mesh;
  private readonly feederMat: SharkMaterial;
  private readonly pilotMesh: THREE.Mesh;
  private readonly pilotMat: SharkMaterial;

  private spawn: FeederSpawn | null = null;
  /** id of the feeder currently in the water, or -1 */
  private liveId = -1;
  /** who holds the pilot fish: 0 nobody, 1 us, or a peer id */
  private holder = 0;
  private bob = 0;
  private pilotPos = new THREE.Vector3();
  private pilotPrimed = false;

  constructor() {
    this.group.name = 'feeder';

    const f = makeFish(0x51e7, 2, 4, FEEDER_LEN);
    this.feederMat = new SharkMaterial({ jaw: false });
    this.feederMat.setSpineLength(f.spine);
    this.feederMesh = new THREE.Mesh(f.geometry, this.feederMat);
    this.feederMesh.visible = false;
    this.feederMesh.frustumCulled = false;
    this.group.add(this.feederMesh);

    /* The prize. Palette 3 is the warm one, and the material's rim light does the rest — a
       gold fish that only reads as gold in bright water is not a prize at night. */
    const p = makeFish(0x901d, 3, 0, FEEDER_LEN * 0.62);
    this.pilotMat = new SharkMaterial({ jaw: false });
    this.pilotMat.setSpineLength(p.spine);
    this.pilotMesh = new THREE.Mesh(p.geometry, this.pilotMat);
    this.pilotMesh.visible = false;
    this.pilotMesh.frustumCulled = false;
    this.group.add(this.pilotMesh);
  }

  /** the feeder in the water, or null */
  get live(): FeederSpawn | null {
    return this.liveId >= 0 ? this.spawn : null;
  }

  get heldBy(): number {
    return this.holder;
  }

  /** a feeder has appeared */
  show(s: FeederSpawn): void {
    this.spawn = s;
    this.liveId = s.id;
    this.feederMesh.visible = true;
    this.feederMesh.position.set(s.x, s.y, s.z);
  }

  /** somebody got it. `by` is 1 for us, a peer id otherwise, 0 for nobody. */
  taken(by: number): void {
    this.liveId = -1;
    this.feederMesh.visible = false;
    this.holder = by;
    this.pilotPrimed = false;
  }

  /**
   * Are we close enough to take it?
   *
   * Reach is bigger while the jaws are closing, so a lunge is worth making without being
   * required — a phone player jabbing at a fish with a thumb should still get there, and
   * somebody who times a strike should beat them to it.
   */
  withinReach(x: number, y: number, z: number, biting: boolean): boolean {
    if (this.liveId < 0 || !this.spawn) return false;
    const r = biting ? REACH_BITING : REACH;
    const dx = x - this.spawn.x, dy = y - this.spawn.y, dz = z - this.spawn.z;
    return dx * dx + dy * dy + dz * dz <= r * r;
  }

  /**
   * @param holderPose where the pilot fish's owner is, or null if nobody in view holds it
   */
  update(dt: number, time: number,
         holderPose: { x: number; y: number; z: number; heading: number } | null): void {
    this.bob += dt;

    if (this.feederMesh.visible && this.spawn) {
      /* It hovers and turns on the spot. A feeder that swam would be a chase, and a chase is a
         different game — this one is about who is nearest when it appears. */
      this.feederMesh.position.y = this.spawn.y + Math.sin(this.bob * 1.7) * 0.22;
      this.feederMesh.rotation.y = Math.sin(this.bob * 0.55) * 0.9 + this.bob * 0.25;
      const swim = swimParamsForSpeed(2.2, 6);
      this.feederMat.setSwim(swim.amp * 1.6, swim.speed * 1.5);
      this.feederMat.setTime(time);
    }

    const show = this.holder !== 0 && holderPose !== null;
    this.pilotMesh.visible = show;
    if (!show || !holderPose) return;

    /* Station-keeping, not a leash. It sits off the holder's shoulder and eases into place, so
       it swings wide through a turn and settles again — which is what a real pilot fish does
       and what makes it read as an animal rather than an attachment. */
    const s = Math.sin(holderPose.heading), c = Math.cos(holderPose.heading);
    const wantX = holderPose.x - s * PILOT_BACK + c * PILOT_SIDE;
    const wantZ = holderPose.z - c * PILOT_BACK - s * PILOT_SIDE;
    const wantY = holderPose.y + PILOT_UP + Math.sin(this.bob * 2.1) * 0.16;
    if (!this.pilotPrimed) {
      this.pilotPos.set(wantX, wantY, wantZ);
      this.pilotPrimed = true;
    } else {
      const k = 1 - Math.exp(-6.5 * dt);
      this.pilotPos.x += (wantX - this.pilotPos.x) * k;
      this.pilotPos.y += (wantY - this.pilotPos.y) * k;
      this.pilotPos.z += (wantZ - this.pilotPos.z) * k;
    }
    this.pilotMesh.position.copy(this.pilotPos);
    this.pilotMesh.rotation.y = holderPose.heading;
    const swim = swimParamsForSpeed(5, 6);
    this.pilotMat.setSwim(swim.amp * 1.3, swim.speed * 1.4);
    this.pilotMat.setTime(time);
  }

  setEnv(env: SkyEnv): void {
    this.feederMat.setEnv(env);
    this.pilotMat.setEnv(env);
  }

  setShadow(map: THREE.Texture | null, matrix: THREE.Matrix4, strength: number): void {
    this.feederMat.setShadow(map, matrix, strength);
    this.pilotMat.setShadow(map, matrix, strength);
  }

  dispose(): void {
    this.feederMesh.geometry.dispose();
    this.pilotMesh.geometry.dispose();
    this.feederMat.dispose();
    this.pilotMat.dispose();
    this.group.removeFromParent();
  }
}

/** how often one appears, seconds. Long enough that holding the prize means something. */
export const FEEDER_PERIOD: [number, number] = [40, 75];

/** the lagoon bounds a feeder may use, for anyone who needs to know without importing COAST */
export const FEEDER_BOUNDS = { z: SPAWN_Z, x: SPAWN_X, reefTo: COAST.reefTo };
